/**
 * Shared UI primitives (plan 03 Phase 6). These are the style constants every
 * screen has been re-declaring (`card`, `sectHead`, `fld`, …), promoted to one
 * place — both as typed style objects (drop-in for existing `style={{…}}`
 * code) and as thin components for new code.
 *
 * Adoption rule (CONTRIBUTING.md): NEW code uses these; existing screens
 * migrate opportunistically when touched. No big-bang restyle.
 */
import type { CSSProperties, HTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

/* ---------------- style tokens ---------------- */

/** Standard surface card (the `card` const screens redeclare). */
export const cardStyle: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(20,20,30,.05)',
  overflow: 'hidden',
};

/**
 * A card's HEAD — the wrap, its title and the half-sentence under it.
 *
 * The entity pages had three different card titles: this one used
 * `SectionHead` (Inter 14.5/700), the process page its own Inter 15/700. An
 * audit across the pages is what turned that up; on any one screen it looks
 * deliberate.
 *
 * Pair with `{ ...cardStyle, padding: 0 }` on the outer card — this wrap
 * carries its own padding and bottom divider, so the card itself must not
 * double up on padding — and give the body below it its own padding.
 */
export const cardHeadWrap: CSSProperties = {
  padding: '16px 20px 12px',
  borderBottom: '1px solid var(--divider)',
};
export const cardHeadTitle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--fs-md)',
  lineHeight: 'var(--lh-md)',
  fontWeight: 600,
  letterSpacing: 'var(--ls-heading-tight)',
  color: 'var(--ink)',
};
export const cardHeadSub: CSSProperties = {
  marginTop: 2,
  fontSize: 'var(--fs-sm)',
  lineHeight: 'var(--lh-sm)',
  fontWeight: 500,
  color: 'var(--sub)',
};

/** Section heading (the `sectHead` const). */
export const sectHeadStyle: CSSProperties = {
  margin: 0,
  fontSize: 14.5,
  fontWeight: 700,
  letterSpacing: '-.1px',
  color: 'var(--ink)',
};

/** Text input field (the `fld` const). */
export const fieldStyle: CSSProperties = {
  width: '100%',
  height: 42,
  padding: '0 12px',
  border: '1px solid var(--field)',
  borderRadius: 11,
  outline: 'none',
  fontFamily: 'inherit',
  fontSize: 13.5,
  background: 'var(--surface)',
  color: 'var(--ink)',
};

/** Field label. */
export const fieldLabelStyle: CSSProperties = {
  display: 'block',
  fontSize: 11.5,
  fontWeight: 600,
  letterSpacing: '.2px',
  color: 'var(--ph)',
  marginBottom: 6,
};

/** One-line ellipsis title (13.5px/600) used in list rows and cards. */
export const rowTitleStyle: CSSProperties = {
  fontSize: 13.5,
  fontWeight: 600,
  lineHeight: 1.25,
  letterSpacing: '-.1px',
  color: 'var(--ink)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/** Muted secondary line under a row title. */
export const rowSubStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--ph)',
  marginTop: 2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/** Square icon chip with tinted background (`iconChip` helper screens copy). */
export function iconChipStyle(bg: string, color: string, size: number, radius: number): CSSProperties {
  return {
    width: size,
    height: size,
    borderRadius: radius,
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: bg,
    color,
  };
}

/* ---------------- thin components ---------------- */

export function Card({
  children,
  style,
  ...rest
}: { children: ReactNode } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div style={{ ...cardStyle, ...style }} {...rest}>
      {children}
    </div>
  );
}

export function SectionHead({
  children,
  style,
  ...rest
}: { children: ReactNode } & HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 style={{ ...sectHeadStyle, ...style }} {...rest}>
      {children}
    </h3>
  );
}

export function Field({ style, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input style={{ ...fieldStyle, ...style }} {...rest} />;
}

export function FieldLabel({
  children,
  style,
  ...rest
}: { children: ReactNode } & HTMLAttributes<HTMLLabelElement>) {
  return (
    <label style={{ ...fieldLabelStyle, ...style }} {...rest}>
      {children}
    </label>
  );
}
